import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "../../lib/api";
import PublicShell from "../../components/PublicShell";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Button } from "../../components/ui/button";
import { Checkbox } from "../../components/ui/checkbox";
import { toast } from "sonner";
import { FileText, CheckCircle2, XCircle, Download } from "lucide-react";

export default function ContractReview() {
  const { token } = useParams();
  const [name, setName] = useState("");
  const [cpf, setCpf] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);

  const pdfUrl = `${process.env.REACT_APP_BACKEND_URL}/api/public/contract/${token}/pdf`;

  async function accept(e) {
    e.preventDefault();
    if (!agreed) { toast.error("Marque a caixa 'Li e concordo' para prosseguir"); return; }
    if (!name.trim() || cpf.replace(/\D/g, "").length < 11) {
      toast.error("Nome completo e CPF são obrigatórios."); return;
    }
    setSaving(true);
    try {
      await api.post(`/public/contract/${token}/accept`, {
        accepted_name: name.trim(),
        accepted_cpf: cpf,
      });
      setDone(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao registrar aceite.");
    } finally { setSaving(false); }
  }

  return (
    <PublicShell>
      <section className="max-w-4xl mx-auto px-6 lg:px-10 py-16">
        {done ? (
          <div className="bg-white border border-emerald-200 rounded p-8" data-testid="review-done">
            <div className="flex items-center gap-2 text-emerald-700">
              <CheckCircle2 className="w-6 h-6" />
              <h1 className="text-2xl font-semibold">Aceite registrado</h1>
            </div>
            <p className="text-sm text-slate-600 mt-4">
              Recebemos seu aceite eletrônico. Enviamos uma cópia final assinada para o seu e-mail. Guarde-a para seus arquivos.
            </p>
            <p className="text-xs text-slate-500 mt-4">
              Registro conforme MP 2.200-2/2001 (art. 10, §2º) e Lei 14.063/2020.
            </p>
          </div>
        ) : (
          <>
            <span className="text-[11px] uppercase tracking-[0.28em] text-[#3B9FD4]">Contrato de locação</span>
            <h1 className="font-display text-4xl text-[#0AA89C] mt-2">Revisão e aceite</h1>
            <p className="text-sm text-slate-500 mt-3">
              Leia o documento completo abaixo. Ao final, preencha nome e CPF para registrar seu aceite eletrônico.
            </p>

            <div className="mt-6 bg-white border border-slate-200 rounded-md overflow-hidden" style={{ height: "70vh" }}>
              <iframe
                title="Contrato de locação"
                src={pdfUrl}
                className="w-full h-full"
                data-testid="review-pdf-iframe"
              />
            </div>
            <div className="mt-2 text-xs text-slate-500 flex items-center justify-between">
              <span>Não consegue visualizar? <a href={pdfUrl} target="_blank" rel="noreferrer" className="text-[#0AA89C] hover:text-[#066B63]"><Download className="inline w-3 h-3 mr-1" />Baixar PDF</a></span>
            </div>

            <form onSubmit={accept} className="mt-8 bg-white border border-slate-200 rounded p-6" data-testid="review-form">
              <h2 className="text-sm font-semibold text-[#0AA89C] mb-3">Registro do aceite eletrônico</h2>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Nome completo (conforme documento)</Label><Input value={name} onChange={(e) => setName(e.target.value)} required autoComplete="name" data-testid="accept-name" /></div>
                <div><Label>CPF</Label><Input value={cpf} onChange={(e) => setCpf(e.target.value)} required placeholder="000.000.000-00" data-testid="accept-cpf" /></div>
              </div>
              <label className="mt-4 flex items-start gap-2 text-sm text-[#22302E]">
                <Checkbox checked={agreed} onCheckedChange={setAgreed} data-testid="accept-checkbox" />
                <span>
                  Li integralmente o contrato acima, concordo com todas as suas cláusulas — em especial com a
                  Cláusula 10ª (Condições de Pagamento e Cancelamento), inclusive a retenção de 30% sobre o valor total
                  em caso de desistência — e registro meu aceite eletrônico livre e consciente, nos termos da Lei 14.063/2020.
                </span>
              </label>
              <div className="mt-6 flex justify-end">
                <Button type="submit" disabled={saving || !agreed} className="bg-[#0AA89C] hover:bg-[#066B63]" data-testid="accept-submit">
                  {saving ? "Registrando…" : "Li e concordo — registrar aceite"}
                </Button>
              </div>
              <p className="mt-4 text-xs text-slate-500">
                Ao registrar, guardaremos data/hora, endereço IP, agente do navegador e o hash SHA-256 do documento exatamente como você o leu — como evidência do aceite.
              </p>
            </form>
          </>
        )}
      </section>
    </PublicShell>
  );
}
